import * as React from 'react';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';

/** Material UI Packages. */
import { Box, Button, Typography, TextField, Select, MenuItem, Stack, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Library for optimizing performance.*/
import { debounce } from 'lodash';

/** Internal Components. */
import { AutocompleteField_String } from 'shared/ui/AutoCompleteForm';
import { SelectField_single_None } from 'shared/ui/SelectForm';
import TooltipTypography from 'shared/ui/Typography';
import SingleDatagrid from 'shared/ui/Datagrid';
import { client_closure, case_closure, mbl_release } from 'shared/config/constantsData';
import { handleProcessRowUpdate, readSocketData, updateSocketData } from 'shared/lib/fetchSocket';
import useUserRole from 'shared/hooks/useUserRole';
import { formatOperationDateTime, formatToMMDDYY } from 'shared/lib/dateFormatter';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import UpdateRateInfo from '../Rate/UpdateRateInfo';
import {
    bookingConfirmationCol,
    arrivalNoticeCol,
    invoiceCol,
    oceanFreightCol,
    airFreightCol,
    crdrCol,
    siCol,
    aesCol,
    dgHazmatCol,
} from '../TableDefinition/CommonSubDef';
import { notAllowNullKey, dateField } from 'data/shipmentDetailConfig';
import { isManagerOrAbove } from 'shared/lib/roles';
import { useShipmentTodos } from '../../lib/useShipmentTodos';

const UpdateStatement = (props) => {
    /** Get props from ShipmentDetail.js */
    const { row, presetData, statementConfig } = props;

    /** Get userRole data from auth0. */
    const userRole = useUserRole();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** All history list of value changes. */
    const historyData = useSelector((state) => state.historyList);
    /** Shipment type value (0~4). */
    const tabValue = useSelector((state) => state.tabValueForm);
    /** Original main shipment list. */
    const formData = useSelector((state) => state[statementConfig.formData]);
    /** Matching HBL shipment list based on the main data's ID value. */
    const mainShipmentData = formData.filter((item) => item.id === row.id);
    /** Original rate data based on shipment. */
    const rateData = useSelector((state) => state[statementConfig.rateData]);
    /** To-do list for the currently selected shipment. */
    const todoList = useShipmentTodos(tabValue, row.id);
    /** Check if case_closure is Complete */
    const isCaseClosureComplete = mainShipmentData[0]?.case_closure === 'Complete';

    /** State to store autocomplete options for Rail companies. */
    const [railCompanyOptions, setRailCompanyOptions] = useState({ intermodal: [], cy_location: [], warehouse: [] });
    /** State to manage the visibility of the rate dialog. */
    const [rateOpen, setRateOpen] = useState(false);
    /** State to control whether to show a badge. */
    const [showBadge, setShowBadge] = useState(false);
    const [internalNote, setInternalNote] = useState(row.internal_note || '');
    const [isNoteExpanded, setIsNoteExpanded] = useState(false);
    const [intermodal, setIntermodal] = useState(presetData?.intermodal);

    /** Define table column structures using preset data and to-do list. */
    const bookingConfirmationColsBase = bookingConfirmationCol(presetData, todoList);
    const arrivalNoticeColsBase = arrivalNoticeCol(presetData, todoList);
    const oceanFreightColsBase = oceanFreightCol(presetData, todoList);
    const airFreightColsBase = airFreightCol(presetData, todoList);
    const siColsBase = siCol(presetData, todoList);
    const aesColsBase = aesCol(presetData, todoList);
    const dgHazmatColsBase = dgHazmatCol(presetData, todoList);
    const invoiceColsBase = invoiceCol(presetData, userRole, todoList);
    const crdrColsBase = crdrCol(presetData, userRole, todoList);

    /** Helper function to disable all columns if case_closure is Complete */
    const disableColumnsIfComplete = (cols) => {
        if (!isCaseClosureComplete) return cols;
        return cols.map((col) => ({
            ...col,
            editable: false,
        }));
    };

    const bookingConfirmationCols = useMemo(
        () => disableColumnsIfComplete(bookingConfirmationColsBase),
        [bookingConfirmationColsBase, isCaseClosureComplete]
    );
    const arrivalNoticeCols = useMemo(
        () => disableColumnsIfComplete(arrivalNoticeColsBase),
        [arrivalNoticeColsBase, isCaseClosureComplete]
    );
    const oceanFreightCols = useMemo(
        () => disableColumnsIfComplete(oceanFreightColsBase),
        [oceanFreightColsBase, isCaseClosureComplete]
    );
    const airFreightCols = useMemo(
        () => disableColumnsIfComplete(airFreightColsBase),
        [airFreightColsBase, isCaseClosureComplete]
    );
    const siCols = useMemo(() => disableColumnsIfComplete(siColsBase), [siColsBase, isCaseClosureComplete]);
    const aesCols = useMemo(() => disableColumnsIfComplete(aesColsBase), [aesColsBase, isCaseClosureComplete]);
    const dgHazmatCols = useMemo(
        () => disableColumnsIfComplete(dgHazmatColsBase),
        [dgHazmatColsBase, isCaseClosureComplete]
    );
    const invoiceCols = useMemo(
        () => disableColumnsIfComplete(invoiceColsBase),
        [invoiceColsBase, isCaseClosureComplete]
    );
    const crdrCols = useMemo(() => disableColumnsIfComplete(crdrColsBase), [crdrColsBase, isCaseClosureComplete]);

    /** Update the badge visibility based on rate data. */
    useEffect(() => {
        setShowBadge(rateData.some((item) => item[statementConfig.keyName[0]] === row.id));
    }, [rateData, row.id]);

    const latestInternalNote = mainShipmentData[0]?.internal_note || '';
    useEffect(() => {
        setInternalNote(latestInternalNote);
    }, [row.id, latestInternalNote]);

    useEffect(() => {
        const intermodalPreset = mainShipmentData[0]?.il_opus_reference_preset?.intermodal;
        setIntermodal(intermodalPreset);
    }, [mainShipmentData]);

    /** Open the rate modal. */
    const handleOpen = () => setRateOpen(true);

    /** Handle to change autocomplete Rail company and call Websocket to receive the searched result. */
    const handleSocketReadRailCompany = useCallback(
        (fieldName, event, searchValue) => {
            const onSuccess = (receivedData) => {
                setRailCompanyOptions((prev) => ({ ...prev, [fieldName]: receivedData }));
            };
            readSocketData(dispatch, tabValue, 'cell', 'il_trade_partner', fieldName, searchValue ?? '', onSuccess);
        },
        [dispatch, tabValue]
    );

    /** Handle change in select, autocomplete, textarea field and update via WebSocket. */
    const handleSocketUpdate = useCallback(
        (key, value) => {
            // Prevent updating any field (except case_closure) if case_closure is Complete
            if (isCaseClosureComplete && key !== 'case_closure') {
                dispatch(
                    setSnackbar({
                        open: true,
                        message:
                            'Cannot modify fields when Case Closure is Complete. Please change Case Closure first.',
                        severity: 'warning',
                    })
                );
                return;
            }

            const sendValue = value ? value : '';
            if (!mainShipmentData) return;
            updateSocketData(dispatch, tabValue, 'cell', statementConfig.tableName, statementConfig.reducerActions[0], {
                id: mainShipmentData[0]?.id,
                key: key,
                value: sendValue,
            });
        },
        [
            dispatch,
            tabValue,
            statementConfig.tableName,
            statementConfig.reducerActions,
            isCaseClosureComplete,
            mainShipmentData,
        ]
    );

    /** Ref so debounced fn always calls the latest handleSocketUpdate (correct id, closure state). */
    const handleSocketUpdateRef = useRef(handleSocketUpdate);
    handleSocketUpdateRef.current = handleSocketUpdate;

    /** Debounce socket emits; ref avoids stale handler when row props change while the debounce was created once. */
    const debouncedSocketUpdate = useMemo(
        () =>
            debounce((key, value) => {
                handleSocketUpdateRef.current(key, value);
            }, 1000),
        []
    );

    useEffect(() => {
        return () => {
            debouncedSocketUpdate.cancel();
        };
    }, [debouncedSocketUpdate]);

    const columnMappings = {
        bookingConfirmationCols,
        arrivalNoticeCols,
        oceanFreightCols,
        airFreightCols,
        siCols,
        aesCols,
        dgHazmatCols,
        invoiceCols,
        crdrCols,
    };

    return (
        <>
            {presetData ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignContent: 'center',
                        gap: '5px',
                        flex: 1,
                        minHeight: 0,
                        height: '100%',
                    }}
                >
                    {statementConfig.hasCY && (
                        <AutocompleteField_String
                            label={'CY Location'}
                            name={'cy_location'}
                            value={mainShipmentData[0]?.cy_location}
                            onChange={(event, newValue) => handleSocketUpdate('cy_location', newValue)}
                            onInputChange={(event, value) => handleSocketReadRailCompany('cy_location', event, value)}
                            onFocus={(event) => handleSocketReadRailCompany('cy_location', event, '')}
                            options={railCompanyOptions.cy_location}
                            freeSolo={false}
                            disable={isCaseClosureComplete}
                            AutocompleteStyle={{
                                width: '90%',
                            }}
                            sx={{ gap: 1 }}
                            labelStyle={{ fontSize: '12px' }}
                            disableClearable={false}
                        />
                    )}
                    {statementConfig.hasRail && (
                        <AutocompleteField_String
                            label={'Rail Location'}
                            name={'intermodal'}
                            value={mainShipmentData[0]?.intermodal}
                            onChange={(event, newValue) => handleSocketUpdate('intermodal', newValue)}
                            onInputChange={(event, value) => handleSocketReadRailCompany('intermodal', event, value)}
                            onFocus={(event) => handleSocketReadRailCompany('intermodal', event, '')}
                            options={railCompanyOptions.intermodal}
                            freeSolo={false}
                            disable={!intermodal || isCaseClosureComplete}
                            AutocompleteStyle={{
                                width: '90%',
                            }}
                            sx={{ gap: 1 }}
                            labelStyle={{ fontSize: '12px' }}
                            disableClearable={false}
                        />
                    )}
                    {statementConfig.hasCFS && (
                        <AutocompleteField_String
                            label={'CFS Location'}
                            name={'cfs_location'}
                            value={mainShipmentData[0]?.cfs_location}
                            onChange={(event, newValue) => handleSocketUpdate('cfs_location', newValue)}
                            onInputChange={(event, value) => handleSocketReadRailCompany('warehouse', event, value)}
                            onFocus={(event) => handleSocketReadRailCompany('warehouse', event, '')}
                            options={railCompanyOptions.warehouse}
                            freeSolo={false}
                            disable={isCaseClosureComplete}
                            AutocompleteStyle={{
                                width: '90%',
                            }}
                            sx={{ gap: 1 }}
                            labelStyle={{ fontSize: '12px' }}
                            disableClearable={false}
                        />
                    )}
                    {statementConfig.hasMBL && (
                        <SelectField_single_None
                            label={'MBL Release'}
                            name={'mbl_release'}
                            value={mainShipmentData[0]?.mbl_release}
                            onChange={(event) => handleSocketUpdate('mbl_release', event.target.value)}
                            options={mbl_release}
                            disabled={isCaseClosureComplete}
                            sx={{ gap: 1, mb: 0.5 }}
                            labelStyle={{ fontSize: '12px' }}
                            selectStyle={{ width: '90%' }}
                        />
                    )}

                    {statementConfig.sections.map(({ label, cols, history }) => {
                        const matchedHistory =
                            historyData[tabValue]?.length > 0
                                ? historyData[tabValue]
                                      ?.filter((item) => item.table_id === row.id && history.includes(item.attribute))
                                      .sort((a, b) => a.id - b.id)
                                : [];
                        const fomattedHistory =
                            matchedHistory.length > 0
                                ? matchedHistory
                                      .map(
                                          (item) =>
                                              `${formatOperationDateTime(item.modified_on, 'M/D/YYYY')}: ${formatToMMDDYY(
                                                  item.value
                                              )} ${item.registered_by}`
                                      )
                                      .join('<br />')
                                : '';

                        return (
                            <Stack spacing={1} key={label}>
                                <TooltipTypography label={label} placement={'right'} tooltipLabel={fomattedHistory} />
                                <SingleDatagrid
                                    rows={mainShipmentData}
                                    cols={columnMappings[cols]}
                                    maxHeight={'62px'}
                                    processRowUpdate={(newRow) => {
                                        // Prevent updating if case_closure is Complete
                                        if (isCaseClosureComplete) {
                                            dispatch(
                                                setSnackbar({
                                                    open: true,
                                                    message:
                                                        'Cannot modify fields when Case Closure is Complete. Please change Case Closure first.',
                                                    severity: 'warning',
                                                })
                                            );
                                            return Promise.resolve(
                                                mainShipmentData.find((r) => r.id === newRow.id) || newRow
                                            );
                                        }
                                        return handleProcessRowUpdate({
                                            dispatch,
                                            tabValue,
                                            methodTableName: statementConfig.tableName,
                                            reducerName: statementConfig.reducerActions[0],
                                            tableId: [],
                                            formData,
                                            newRow,
                                            notAllowNullKey,
                                            dateField,
                                        });
                                    }}
                                />
                            </Stack>
                        );
                    })}
                    {statementConfig.hasRate && (
                        <Button
                            sx={{
                                height: '40px',
                                width: '100%',
                                color: '#003a52',
                                fontSize: '13px',
                                border: '1px solid #003a52',
                                mt: 1,
                                ':hover': { bgcolor: 'rgb(0,100,126)', color: 'white' },
                            }}
                            onClick={handleOpen}
                        >
                            Rate Info
                            {showBadge && (
                                <Box
                                    sx={{
                                        bgcolor: 'red',
                                        color: 'white',
                                        fontSize: '9px',
                                        width: '14px',
                                        height: '14px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        ml: 0.5,
                                    }}
                                >
                                    n
                                </Box>
                            )}
                        </Button>
                    )}
                    {statementConfig.hasClose && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <Typography sx={{ fontSize: 12, color: '#003a52', fontWeight: 500 }}>
                                    Internal Note
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => setIsNoteExpanded((prev) => !prev)}
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        fontSize: 12,
                                        border: '1px solid #b0b0b0',
                                        color: '#003a52',
                                    }}
                                >
                                    {isNoteExpanded ? '−' : '+'}
                                </IconButton>
                            </Box>
                            <TextField
                                label=""
                                value={internalNote}
                                onChange={(e) => {
                                    if (isCaseClosureComplete) {
                                        dispatch(
                                            setSnackbar({
                                                open: true,
                                                message:
                                                    'Cannot modify fields when Case Closure is Complete. Please change Case Closure first.',
                                                severity: 'warning',
                                            })
                                        );
                                        return;
                                    }
                                    setInternalNote(e.target.value);
                                    debouncedSocketUpdate('internal_note', e.target.value);
                                }}
                                disabled={isCaseClosureComplete}
                                multiline
                                minRows={isNoteExpanded ? 15 : 5}
                                maxRows={isNoteExpanded ? 15 : 5}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        fontSize: '11px',
                                        p: 0.5,
                                    },
                                }}
                            />
                            <Grid container spacing={1}>
                                {[
                                    {
                                        label: 'Client Closure',
                                        name: 'clientClosure',
                                        value: 'client_closure',
                                        handler: (event) => handleSocketUpdate('client_closure', event.target.value),
                                        options: client_closure,
                                    },
                                    {
                                        label: 'Case Closure',
                                        name: 'caseClosure',
                                        value: 'case_closure',
                                        handler: (event) => handleSocketUpdate('case_closure', event.target.value),
                                        options: case_closure,
                                    },
                                ].map(({ label, name, value, handler, options }) => {
                                    const matchedHistory =
                                        historyData[tabValue]?.length > 0
                                            ? historyData[tabValue]
                                                  .filter(
                                                      (item) => item.table_id === row.id && item.attribute === value
                                                  )
                                                  .sort((a, b) => a.id - b.id)
                                            : [];
                                    const fomattedHistory =
                                        matchedHistory.length > 0
                                            ? matchedHistory
                                                  .map(
                                                      (item) =>
                                                          `${formatOperationDateTime(item.modified_on, 'M/D/YYYY')}: ${
                                                              item.value
                                                          } ${item.registered_by}`
                                                  )
                                                  .join('<br />')
                                            : '';

                                    return (
                                        <Grid key={name} size={6}>
                                            <TooltipTypography
                                                label={label}
                                                placement={'right'}
                                                tooltipLabel={fomattedHistory}
                                            />

                                            <Select
                                                onChange={handler}
                                                name={name}
                                                value={mainShipmentData.find((data) => data)?.[value] || ''}
                                                disabled={
                                                    (name === 'caseClosure' && !isManagerOrAbove(userRole)) ||
                                                    (name === 'clientClosure' && isCaseClosureComplete)
                                                }
                                                sx={{
                                                    width: '100%',
                                                    fontSize: '13px',
                                                    mt: 1,
                                                    '& .MuiSelect-select': {
                                                        p: 1,
                                                    },
                                                    height: '27px',
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: { maxHeight: 200 },
                                                    },
                                                }}
                                            >
                                                {options.map((item) => (
                                                    <MenuItem
                                                        key={item.id || item}
                                                        value={item}
                                                        sx={{ fontSize: '13px' }}
                                                    >
                                                        {item}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </>
                    )}

                    <UpdateRateInfo row={row} rateOpen={rateOpen} setRateOpen={setRateOpen} />
                </Box>
            ) : (
                <></>
            )}
        </>
    );
};
export default React.memo(UpdateStatement);
